import React, { useMemo, useRef, useState } from 'react'
import { AppFieldWrapper, AppLabelWrapper } from 'components/AppAccount/AppAccount.style'
import { AppFC } from 'types'
import { AppToolTipIcon } from 'components/Common/AppToolTip/AppToolTipIcon'
import { AppToolTip } from 'components/Common/AppToolTip/AppToolTip.component'
import { UserFile, UserFileTag } from '__generated__/api-types-and-hooks'
import AppFilePlaceholderIcon from 'components/Common/AppSvgIcons/AppFilePlaceholderIcon'
import { AppButton } from 'components/Common/AppButton/AppButton.component'
import { AppBinIcon } from 'components/Common/AppSvgIcons/AppBinIcon'
import { uploadFiles } from 'components/AppFileUploader/AppFileUploader.helper'
import { getTenantId } from 'utils/getTenantId'
import { useSelector } from 'react-redux'
import { RootState } from 'App'
import GradientCircleNotchIcon from 'components/Common/SvgIcons/GradientCircleNotchIcon'

// Create a wrapper component that adapts AppBinIcon to the button's icon requirements
const BinIconWrapper = (props: { color?: string }) => <AppBinIcon stroke={props.color} />

interface IAppViewBusinessProfileLogo {
  LabelIcon?: React.ComponentType
  label?: string
  value?: UserFile | null
  toolTipText?: string
  isEditMode?: boolean
  onLogoUpdated?: (logo: UserFile | null) => void
  isUpdating?: boolean
  fileTag?: UserFileTag
}

export const AppViewBusinessProfileLogo: AppFC<IAppViewBusinessProfileLogo> = ({
  label,
  value,
  LabelIcon,
  toolTipText,
  isEditMode,
  onLogoUpdated,
  isUpdating,
  fileTag,
}) => {
  const tenantId = getTenantId()
  const userId = useSelector((state: RootState) => state.user.user.id)
  const clientId = useSelector((state: RootState) => state.clients.client.profile.id)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return

    // Validate file format (only images)
    const allowedFormats = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
    if (!allowedFormats.includes(files[0].type)) {
      setError('Only image files are allowed (jpeg, png, gif, webp)')
      fileInputRef.current!.value = ''
      return
    }

    // Validate file size (max 5MB)
    const maxSizeMB = 5
    if (files[0].size > maxSizeMB * 1024 * 1024) {
      setError(`File size must be less than ${maxSizeMB}MB`)
      fileInputRef.current!.value = ''
      return
    }

    handleUpload(files[0])
  }

  const handleUpload = async (file: File) => {
    try {
      setIsUploading(true)
      setError(null)

      const uploadedFiles = await uploadFiles({
        files: [file],
        tenantId,
        clientId,
        userId,
        fileTag,
      })

      if (uploadedFiles && uploadedFiles.length > 0) {
        const uploadedLogo = {
          ...uploadedFiles[0],
          createdAt: new Date().toISOString(),
        }

        // Call the callback to update the logo in parent component/store
        onLogoUpdated && onLogoUpdated(uploadedLogo)
      }
    } catch (error) {
      console.error('Error uploading logo:', error)
      setError('Failed to upload the image. Please try again.')
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleDelete = () => {
    setError(null)
    onLogoUpdated && onLogoUpdated(null)
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)

    const files = Array.from(e.dataTransfer.files)
    if (files.length === 0) return

    // Only use the first file if multiple files are dropped
    const file = files[0]

    // Use existing file validation logic
    const allowedFormats = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
    if (!allowedFormats.includes(file.type)) {
      setError('Only image files are allowed (jpeg, png, gif, webp)')
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB')
      return
    }

    handleUpload(file)
  }

  const isUpdateProcessing: boolean = useMemo(
    () => isUpdating || isUploading,
    [isUpdating, isUploading]
  )

  return (
    <AppFieldWrapper>
      {LabelIcon && <LabelIcon />}
      <AppLabelWrapper>
        {label}
        {toolTipText && (
          <AppToolTip name={'TextFieldToolTip'} text={toolTipText} variant="primary" place="bottom">
            <AppToolTipIcon />
          </AppToolTip>
        )}
      </AppLabelWrapper>
      <div className="flex items-center gap-4 mb-6">
        <div
          className={`w-24 ${isDragging ? 'ring-2 ring-app-purple-100' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {value?.url ? (
            <img src={value?.url ?? ''} alt="Business logo" className="w-24 h-24 rounded-2xl" />
          ) : (
            <AppFilePlaceholderIcon />
          )}
        </div>
        {isEditMode && (
          <div className="flex flex-col gap-2">
            <AppButton
              size="sm"
              label="Upload picture"
              variant="primary"
              onClick={handleUploadClick}
              disabled={isUpdateProcessing}
            />
            <AppButton
              size="sm"
              label="Delete"
              variant="secondary"
              LeftIcon={BinIconWrapper}
              onClick={handleDelete}
              disabled={isUpdateProcessing || !value?.url}
            />

            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept="image/*"
              onChange={handleFileSelect}
            />
          </div>
        )}

        {isUpdateProcessing && (
          <div className="flex items-center gap-2 ml-4">
            <GradientCircleNotchIcon width="24" height="24" />
            <span className="text-sm">Processing...</span>
          </div>
        )}
      </div>
      <div className="mb-10">
        {isEditMode && (
          <div className="text-app-grey-70 text-sm my-2">
            Click to update or drag your image here into the square
          </div>
        )}
        {error && <div className="text-app-red-5 text-sm my-2">{error}</div>}
      </div>
    </AppFieldWrapper>
  )
}
